import { useState, useEffect, useLayoutEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import * as Sentry from '@sentry/browser';

/**
 * Live brewery document from Firestore.
 * @param {string|undefined} id - Brewery document id
 * @returns {{ brewery: object|null, isLoading: boolean, exists: boolean|null, error: Error|null }}
 */
function useBrewerySnapshot(id) {
  const [state, setState] = useState(() => ({
    brewery: null,
    isLoading: Boolean(id),
    exists: null,
    error: null,
  }));

  useLayoutEffect(() => {
    if (!id) {
      setState({
        brewery: null,
        isLoading: false,
        exists: false,
        error: null,
      });
      return;
    }
    setState({
      brewery: null,
      isLoading: true,
      exists: null,
      error: null,
    });
  }, [id]);

  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const docRef = doc(db, 'breweries', id);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists) {
          setState({
            brewery: null,
            isLoading: false,
            exists: false,
            error: null,
          });
          return;
        }
        setState({
          brewery: snapshot.data(),
          isLoading: false,
          exists: true,
          error: null,
        });
      },
      (err) => {
        Sentry.captureException(err);
        setState({
          brewery: null,
          isLoading: false,
          exists: null,
          error: err,
        });
      },
    );

    return () => unsubscribe();
  }, [id]);

  return state;
}

export default useBrewerySnapshot;
